import { AlertTriangle, AlertCircle, CheckCircle2 } from "lucide-react";
import { fmtMoney, fmtPct, fmtNum } from "@/lib/format";

const SEVERITY = {
  danger: {
    icon: AlertTriangle,
    borderCls: "border-l-[color:var(--bad)]",
    labelCls: "text-[color:var(--bad)]",
    iconCls: "text-[color:var(--bad)]",
    label: "Priority fix",
  },
  warning: {
    icon: AlertCircle,
    borderCls: "border-l-[color:var(--warn)]",
    labelCls: "text-[color:var(--warn)]",
    iconCls: "text-[color:var(--warn)]",
    label: "Worth reviewing",
  },
  success: {
    icon: CheckCircle2,
    borderCls: "border-l-[color:var(--green)]",
    labelCls: "text-[color:var(--green)]",
    iconCls: "text-[color:var(--green)]",
    label: "Doing well",
  },
};

const renderValue = (finding, cur) => {
  if (finding.value == null || finding.value === 0) return null;
  switch (finding.valueType) {
    case "currency": return fmtMoney(cur, finding.value);
    case "percent": return fmtPct(finding.value, 1);
    case "count":
    case "number": return fmtNum(finding.value);
    default:
      return typeof finding.value === "number" ? fmtNum(finding.value) : String(finding.value);
  }
};

export const FindingsSection = ({ findings, cur }) => {
  if (!findings || findings.length === 0) return null;
  return (
    <section className="pt-4 pb-12" data-testid="findings-section">
      <div className="mb-8">
        <p className="ss-kicker mb-3">What we found</p>
        <h2 className="font-display text-3xl sm:text-4xl text-[color:var(--ink)]">
          {findings.length} finding{findings.length === 1 ? "" : "s"} across your account
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {findings.map((f) => {
          const meta = SEVERITY[f.severity] || SEVERITY.warning;
          const Icon = meta.icon;
          const val = renderValue(f, cur);
          return (
            <a
              key={f.id}
              href={`#sec-${f.section}`}
              className={`ss-panel border-l-4 ${meta.borderCls} p-5 no-underline hover:bg-[color:var(--green-soft)]/40 transition-colors`}
              data-testid={`finding-${f.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className={`ss-mono text-[10px] font-semibold uppercase tracking-[0.16em] ${meta.labelCls} mb-2 inline-flex items-center gap-1.5`}>
                    <Icon size={12} strokeWidth={2.25} className={meta.iconCls} />
                    {meta.label}
                  </p>
                  <p className="text-base font-semibold text-[color:var(--ink)] leading-snug">{f.title}</p>
                  {f.detail && (
                    <p className="text-sm text-[color:var(--grey)] mt-2 leading-relaxed">{f.detail}</p>
                  )}
                </div>
                {val && (
                  <span className="shrink-0 ss-mono text-base font-semibold text-[color:var(--ink)] tabular-nums">
                    {val}
                  </span>
                )}
              </div>
            </a>
          );
        })}
      </div>
    </section>
  );
};
